import classNames from "classnames";
import useTranslation from "../../hooks/useTranslation";
import SurveyDispatch from "../../models/surveyDispatch";
import { orangeColor, stoneBlue } from "../../styles/theme";
import { InvalidRespondentsResponse } from "../../types/survey";
import { ProgressBar } from "../ui/ProgressBar/ProgressBar";
import { Typography } from "../ui/Typography/Typography";
import { IconTypes } from "../ui/icons";
import { Icon } from "../ui/icons/Icon";
import "./SurveyDispatchEngagement.scss";

interface SurveyDispatchEngagementProps {
  surveyDispatch: SurveyDispatch;
  invalidRespondents?: InvalidRespondentsResponse;
  invalidRespondentsError?: boolean;
}

export const SurveyDispatchEngagement = ({
  surveyDispatch,
  invalidRespondents,
  invalidRespondentsError,
}: SurveyDispatchEngagementProps) => {
  const { t } = useTranslation("dashboardPage");

  if (!surveyDispatch || surveyDispatch.hasNotLaunched()) return null;

  const engagementPercentage = surveyDispatch.getEngagementPercentage();
  const engagementText = surveyDispatch.isPublic
    ? t("workplaceCultureWidget.latestCard.engagementPublic", {
        engagement: surveyDispatch.engagement,
      })
    : t("workplaceCultureWidget.latestCard.engagementPrivate", {
        engagement: surveyDispatch.engagement,
        recipientsCount: surveyDispatch.recipientsCount,
      });

  const engagementClasses = classNames("SurveyDispatchEngagement__progress", {
    "SurveyDispatchEngagement__progress--public": surveyDispatch.isPublic,
  });

  const invalidRespondentsClasses = classNames(
    "SurveyDispatchEngagement__invalidRespondents",
    {
      "SurveyDispatchEngagement__progress--public": surveyDispatch.isPublic,
    }
  );

  return (
    <div className="SurveyDispatchEngagement">
      {engagementPercentage <= 100 && !surveyDispatch.isPublic && (
        <ProgressBar
          progress={surveyDispatch.getEngagementPercentage()}
          labelPosition={"center"}
          color={stoneBlue}
        />
      )}
      <Typography
        className={engagementClasses}
        desktop={surveyDispatch.isPublic ? "body1" : "body2"}
        mobile={surveyDispatch.isPublic ? "body2" : "caption"}
        weight={surveyDispatch.isPublic ? "bold" : "regular"}
        color={surveyDispatch.isPublic ? "stoneBlue" : "primary"}
      >
        {surveyDispatch.isPublic && (
          <Icon type={IconTypes.Audience} size={16} color={stoneBlue} />
        )}
        {engagementText}
      </Typography>
      {invalidRespondents && invalidRespondents.invalid_respondents_count > 0 && (
        <div className={invalidRespondentsClasses}>
          <Typography
            className="SurveyDispatchEngagement__invalidRespondents__text"
            desktop="caption"
            fontStyle="italic"
          >
            {t("workplaceCultureWidget.latestCard.invalidRespondents", {
              count: invalidRespondents.invalid_respondents_count,
            })}
          </Typography>
        </div>
      )}
      {!!invalidRespondentsError && (
        <div className="SurveyDispatchEngagement__invalidRespondents">
          <Typography
            className="SurveyDispatchEngagement__invalidRespondents__text"
            desktop="caption"
            color="error"
            fontStyle="italic"
          >
            {t("workplaceCultureWidget.latestCard.invalidRespondentsError")}
          </Typography>
        </div>
      )}
    </div>
  );
};
